import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'ru' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Переводы
const translations: Record<Language, Record<string, string>> = {
  ru: {
    // Navigation
    'nav.home': 'Главная',
    'nav.tests': 'Тесты',
    'nav.results': 'Результаты',
    'nav.admin': 'Админ-панель',
    'nav.menu': 'Меню',
    'nav.logout': 'Выйти',
    'nav.portal': 'На портал',
    
    // Common
    'common.welcome': 'Добро пожаловать',
    'common.loading': 'Загрузка...',
    'common.save': 'Сохранить',
    'common.cancel': 'Отмена',
    'common.delete': 'Удалить',
    'common.edit': 'Редактировать',
    'common.add': 'Добавить',
    'common.search': 'Поиск',
    
    // Dashboard
    'dashboard.title': 'Добро пожаловать',
    'dashboard.subtitle': 'Система тестирования FEBEST — для всех сотрудников',
    'dashboard.instruction.title': 'Инструкция по прохождению теста',
    'dashboard.instruction.text': 'В разделе «Тесты» доступны тесты по ассортименту и моделям автомобилей. У каждого теста указаны количество вопросов и лимит времени. После ответа нажмите «Проверить ответ», при необходимости прочитайте пояснение, затем «Далее». Результаты и история попыток — в разделе «Результаты».',
    'dashboard.stats.attempts': 'Всего попыток',
    'dashboard.stats.passed': 'Сдано тестов',
    'dashboard.stats.average': 'Средний балл',
    'dashboard.instructions.title': 'Инструкция для участников тестирования',
    'dashboard.instructions.passing.title': 'Как пройти тест',
    'dashboard.instructions.passing.step1': 'В личном кабинете найдите блок «Назначенные тесты»: у каждого теста указаны количество вопросов, время и срок выполнения',
    'dashboard.instructions.passing.step2': 'Нажмите «Начать» рядом с тестом. Для начатой попытки используйте «Продолжить»',
    'dashboard.instructions.passing.step3': 'Прочитайте вопрос, выберите ответ и нажмите «Проверить ответ» — отобразится правильность и пояснение (если есть)',
    'dashboard.instructions.passing.step4': 'Нажмите «Далее» для перехода к следующему вопросу или «Назад» для возврата. Следите за таймером вверху экрана',
    'dashboard.instructions.passing.step5': 'После последнего вопроса нажмите «Завершить тест» и просмотрите итог',
    'dashboard.instructions.passing.step6': 'В блоке «Последние результаты» доступны ваши попытки и баллы. Нажмите на результат для детализации, а «Вся история» раскроет остальные попытки',
    'dashboard.instructions.passing.step7': 'Для прохождения теста используйте онлайн-режим в системе',
    'dashboard.instructions.types.title': 'Типы вопросов',
    'dashboard.instructions.types.single.title': 'Одиночный выбор',
    'dashboard.instructions.types.single.desc': 'Выберите один правильный ответ из предложенных вариантов',
    'dashboard.instructions.types.multiple.title': 'Множественный выбор',
    'dashboard.instructions.types.multiple.desc': 'Отметьте все правильные варианты',
    'dashboard.instructions.types.open.title': 'Открытый вопрос',
    'dashboard.instructions.types.open.desc': 'Введите ответ в текстовое поле',
    'dashboard.instructions.types.matching.title': 'Соответствие',
    'dashboard.instructions.types.matching.desc': 'Сопоставьте элементы из левого столбца с элементами из правого',
    'dashboard.instructions.types.image.title': 'Определение по изображению',
    'dashboard.instructions.types.image.desc': 'По изображению введите название детали и артикул в формате «Название | Артикул»',
    'dashboard.instructions.admin.title': 'Управление системой (для администраторов)',
    'dashboard.instructions.admin.step1': '«Админ-панель» → «Тесты»: создание, редактирование названия и удаление тестов; внутри теста — добавление и правка вопросов с пояснениями и фото',
    'dashboard.instructions.admin.step2': 'Импорт из Excel и создание теста из текста для массового добавления вопросов',
    'dashboard.instructions.admin.step3': 'Импорт изображений для тестов на определение деталей по фото',
    'dashboard.instructions.admin.step4': 'Раздел «Пользователи»: создание пользователей и назначение прав',
    'dashboard.instructions.admin.step5': 'Раздел «Статистика»: сводка по тестам, пользователям и попыткам',
    'dashboard.instructions.admin.step6': 'Управление попытками: просмотр и при необходимости удаление попыток прохождения',
    'dashboard.instructions.visual.title': 'Схема прохождения',
    'dashboard.instructions.visual.testList': '1. Выбор теста',
    'dashboard.instructions.visual.testInterface': '2. Прохождение теста',
    'dashboard.instructions.visual.results': '3. Просмотр результатов',
    
    // Tests
    'tests.title': 'Доступные тесты',
    'tests.subtitle': 'Выберите тест для прохождения',
    'tests.start': 'Начать тест',
    'tests.questions': 'Вопросов',
    'tests.time': 'Время',
    'tests.minutes': 'мин',
    'tests.noTests': 'Нет доступных тестов',
    'tests.block.products': 'Ассортимент и запчасти',
    'tests.block.vehicles': 'Модели автомобилей',
    'tests.description.empty': 'Описание отсутствует',
    
    // Results
    'results.title': 'Мои результаты',
    'results.stats.title': 'Общая статистика',
    'results.stats.total': 'Всего попыток',
    'results.stats.passed': 'Сдано',
    'results.stats.failed': 'Не сдано',
    'results.stats.average': 'Средний балл',
    'results.stats.time': 'Среднее время',
    'results.noResults': 'У вас пока нет завершенных тестов',
    'results.download': 'Скачать отчет (PDF)',
    'results.showDetails': 'Показать детали',
    'results.hideDetails': 'Скрыть детали',
    'results.details': 'Детализация по вопросам',
    'results.yourAnswer': 'Ваш ответ:',
    'results.correctAnswer': 'Правильный ответ:',
    'results.questionTime': 'Время:',
    'results.correct': 'Правильно',
    'results.incorrect': 'Неправильно',
    'results.date': 'Дата прохождения',
    'results.result': 'Результат',
    'results.time': 'Время прохождения',
    'results.status': 'Статус',
    'results.passed': 'Сдан',
    'results.failed': 'Не сдан',
    
    // Admin
    'admin.title': 'Административная панель',
    'admin.subtitle': 'Управление системой тестирования FEBEST',
    'admin.overview': 'Обзор',
    'admin.users': 'Пользователи',
    'admin.tests': 'Тесты',
    'admin.statistics': 'Статистика',
    'admin.stats.totalUsers': 'Всего пользователей',
    'admin.stats.totalTests': 'Всего тестов',
    'admin.stats.totalAttempts': 'Всего попыток',
    'admin.stats.averageScore': 'Средний балл',
    'admin.users.title': 'Управление пользователями',
    'admin.users.add': 'Добавить пользователя',
    'admin.users.fullName': 'ФИО',
    'admin.users.email': 'Email',
    'admin.users.department': 'Отдел',
    'admin.users.region': 'Регион',
    'admin.users.role': 'Роль',
    'admin.tests.title': 'Управление тестами',
    'admin.tests.import': 'Загрузить из Excel',
    'admin.tests.name': 'Название',
    'admin.tests.block': 'Блок',
    'admin.tests.questions': 'Вопросов',
    'admin.tests.timeLimit': 'Время (мин)',
    'admin.tests.status': 'Статус',
    'admin.tests.statusActive': 'Активен',
    'admin.tests.statusInactive': 'Неактивен',
    'admin.statistics.details': 'Детальная статистика',
    'admin.statistics.description': 'Здесь будет детальная статистика по регионам, вопросам и пользователям',
    'admin.upload.success': 'загружен успешно',
    'admin.upload.error': 'ошибка загрузки',
    'admin.upload.selectBlock': 'Выберите блок теста',
    'admin.upload.format': 'Формат: один лист с колонками: №, Вопрос, Тип, Варианты, Ответ',
    'admin.upload.upload': 'Загрузить',
    'admin.upload.cancel': 'Отмена',
    'admin.tests.exportTemplate': 'Экспорт шаблона',
    'admin.tests.delete': 'Удалить',
    'admin.tests.deleteConfirm': 'Удаление теста',
    'admin.tests.deleteMessage': 'Вы уверены, что хотите удалить тест "{title}"? Это действие нельзя отменить.',
    'admin.tests.deleteSuccess': 'Тест успешно удален',
    'admin.tests.deleteError': 'Ошибка удаления теста',
    'admin.tests.titleUpdated': 'Название теста обновлено',
    'admin.tests.clickToEditName': 'Нажмите, чтобы изменить название',
    'admin.tests.actions': 'Действия',
    
    // Test Page
    'test.timeLeft': 'Осталось времени',
    'test.question': 'Вопрос',
    'test.of': 'из',
    'test.back': 'Назад',
    'test.next': 'Далее',
    'test.checkAnswer': 'Проверить ответ',
    'test.complete': 'Завершить тест',
    'test.passed': 'Тест сдан!',
    'test.failed': 'Тест не сдан',
    'test.startError': 'Не удалось начать тест',
    'test.timeUp': 'Время истекло',
    'test.timeUpMessage': 'Время на прохождение теста истекло. Тест будет завершен автоматически.',
    'test.part': 'Деталь',
    'test.zoom': 'Увеличить',
    'test.enterPartName': 'Введите название детали',
    'test.enterPartNumber': 'Введите артикул',
    'test.enterAnswer': 'Введите ваш ответ',
    'test.matchingInstruction': 'Сопоставьте элементы из левого столбца с элементами из правого столбца',
    'test.selectMatch': 'Выберите соответствие',
    'test.noOptions': 'Нет вариантов для выбора',
    'test.imageIdentification': 'Определите деталь и артикул по изображению:',
    'test.completeError': 'Не удалось завершить тест',
    'test.matchingError': 'Не удалось определить структуру вопроса соответствия. Проверьте данные вопроса.',
    'test.multipleChoiceHint': 'Возможен несколько вариантов',
    'common.error': 'Ошибка',
    
    // AI Generator
    'aiGenerator.title': 'Генератор тестов через AI',
    'aiGenerator.description': 'Используйте искусственный интеллект для автоматической генерации тестов на основе данных о продукции FEBEST, кодировке артикулов, категориях товаров и применимости к автомобилям.',
    'aiGenerator.accessDenied': 'Доступ запрещен',
    'aiGenerator.adminOnly': 'Эта функция доступна только администраторам',
    'aiGenerator.form.title': 'Название теста',
    'aiGenerator.form.titleRequired': 'Введите название теста',
    'aiGenerator.form.titlePlaceholder': 'Например: Тест по кодировке артикулов',
    'aiGenerator.form.description': 'Описание теста',
    'aiGenerator.form.descriptionPlaceholder': 'Описание теста (необязательно)',
    'aiGenerator.form.block': 'Блок теста',
    'aiGenerator.form.difficulty': 'Уровень сложности',
    'aiGenerator.form.questionsCount': 'Количество вопросов',
    'aiGenerator.form.questionsCountPlaceholder': 'От 1 до 100',
    'aiGenerator.form.timeLimit': 'Время на прохождение (минуты)',
    'aiGenerator.form.timeLimitPlaceholder': 'От 5 до 180 минут',
    'aiGenerator.difficulty.easy': 'Легкий',
    'aiGenerator.difficulty.easyDesc': 'Простые вопросы на знание фактов',
    'aiGenerator.difficulty.medium': 'Средний',
    'aiGenerator.difficulty.mediumDesc': 'Вопросы на понимание и применение',
    'aiGenerator.difficulty.hard': 'Сложный',
    'aiGenerator.difficulty.hardDesc': 'Комплексные вопросы на анализ и сопоставление',
    'aiGenerator.generate': 'Сгенерировать тест',
    'aiGenerator.generating': 'Генерация теста...',
    'aiGenerator.generatingDesc': 'Пожалуйста, подождите. AI генерирует вопросы на основе данных о продукции.',
    'aiGenerator.success': 'Тест успешно сгенерирован!',
    'aiGenerator.error': 'Ошибка при генерации теста',
    
    // Footer
    'footer.text': 'FEBEST Testing System',
    'footer.developedBy': 'Разработано Pikin P.',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.tests': 'Tests',
    'nav.results': 'Results',
    'nav.admin': 'Admin Panel',
    'nav.menu': 'Menu',
    'nav.logout': 'Logout',
    'nav.portal': 'Back to Portal',
    
    // Common
    'common.welcome': 'Welcome',
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.search': 'Search',
    
    // Dashboard
    'dashboard.title': 'Welcome',
    'dashboard.subtitle': 'FEBEST Testing System — for all participants',
    'dashboard.instruction.title': 'Test Instructions',
    'dashboard.instruction.text': 'In the "Tests" section you will see available tests by assortment and vehicle models. Each test shows the number of questions and time limit. After answering, click "Check answer", read the explanation if shown, then "Next". Your results and attempt history are in "Results".',
    'dashboard.stats.attempts': 'Total Attempts',
    'dashboard.stats.passed': 'Tests Passed',
    'dashboard.stats.average': 'Average Score',
    'dashboard.instructions.title': 'Instructions for All Participants',
    'dashboard.instructions.passing.title': 'How to Take a Test',
    'dashboard.instructions.passing.step1': 'Find "Assigned Tests" in your personal account: each test shows its question count, time limit, and due date',
    'dashboard.instructions.passing.step2': 'Click "Start" next to the test. Use "Continue" for an attempt you have already started',
    'dashboard.instructions.passing.step3': 'Read the question, select your answer, and click "Check answer" — you will see if it is correct and any explanation',
    'dashboard.instructions.passing.step4': 'Click "Next" to go to the next question or "Back" to go back. Keep an eye on the timer at the top',
    'dashboard.instructions.passing.step5': 'After the last question, click "Complete Test" and review your score',
    'dashboard.instructions.passing.step6': 'Your attempts and scores appear under "Recent Results". Click a result for details, or expand "All History" to see earlier attempts',
    'dashboard.instructions.passing.step7': 'Please use online mode in the system to complete tests',
    'dashboard.instructions.types.title': 'Question Types',
    'dashboard.instructions.types.single.title': 'Single Choice',
    'dashboard.instructions.types.single.desc': 'Select one correct answer from the options',
    'dashboard.instructions.types.multiple.title': 'Multiple Choice',
    'dashboard.instructions.types.multiple.desc': 'Select all correct options',
    'dashboard.instructions.types.open.title': 'Open Question',
    'dashboard.instructions.types.open.desc': 'Enter your answer in the text field',
    'dashboard.instructions.types.matching.title': 'Matching',
    'dashboard.instructions.types.matching.desc': 'Match items from the left column with items from the right column',
    'dashboard.instructions.types.image.title': 'Image Identification',
    'dashboard.instructions.types.image.desc': 'Look at the image and enter the part name and article in the format "Name | Article"',
    'dashboard.instructions.admin.title': 'System Management (for Administrators)',
    'dashboard.instructions.admin.step1': '"Admin Panel" → "Tests": create, edit titles, and delete tests; inside a test, add and edit questions with explanations and images',
    'dashboard.instructions.admin.step2': 'Import from Excel and create test from text to add questions in bulk',
    'dashboard.instructions.admin.step3': 'Import images for tests that identify parts by photo',
    'dashboard.instructions.admin.step4': '"Users" section: create users and assign roles',
    'dashboard.instructions.admin.step5': '"Statistics" section: overview of tests, users, and attempts',
    'dashboard.instructions.admin.step6': 'Attempt management: view and, if needed, delete attempts',
    'dashboard.instructions.visual.title': 'Process Overview',
    'dashboard.instructions.visual.testList': '1. Test Selection',
    'dashboard.instructions.visual.testInterface': '2. Taking the Test',
    'dashboard.instructions.visual.results': '3. Viewing Results',
    
    // Tests
    'tests.title': 'Available Tests',
    'tests.subtitle': 'Select a test to take',
    'tests.start': 'Start Test',
    'tests.questions': 'Questions',
    'tests.time': 'Time',
    'tests.minutes': 'min',
    'tests.noTests': 'No available tests',
    'tests.block.products': 'Products & Parts',
    'tests.block.vehicles': 'Vehicle Models',
    'tests.description.empty': 'No description',
    
    // Results
    'results.title': 'My Results',
    'results.stats.title': 'Overall Statistics',
    'results.stats.total': 'Total Attempts',
    'results.stats.passed': 'Passed',
    'results.stats.failed': 'Failed',
    'results.stats.average': 'Average Score',
    'results.stats.time': 'Average Time',
    'results.noResults': 'You have no completed tests yet',
    'results.download': 'Download Report (PDF)',
    'results.showDetails': 'Show Details',
    'results.hideDetails': 'Hide Details',
    'results.details': 'Question Details',
    'results.yourAnswer': 'Your Answer:',
    'results.correctAnswer': 'Correct Answer:',
    'results.questionTime': 'Time:',
    'results.correct': 'Correct',
    'results.incorrect': 'Incorrect',
    'results.date': 'Completion Date',
    'results.result': 'Result',
    'results.time': 'Time Spent',
    'results.status': 'Status',
    'results.passed': 'Passed',
    'results.failed': 'Failed',
    
    // Admin
    'admin.title': 'Administrative Panel',
    'admin.subtitle': 'FEBEST Testing System Management',
    'admin.overview': 'Overview',
    'admin.users': 'Users',
    'admin.tests': 'Tests',
    'admin.statistics': 'Statistics',
    'admin.stats.totalUsers': 'Total Users',
    'admin.stats.totalTests': 'Total Tests',
    'admin.stats.totalAttempts': 'Total Attempts',
    'admin.stats.averageScore': 'Average Score',
    'admin.users.title': 'User Management',
    'admin.users.add': 'Add User',
    'admin.users.fullName': 'Full Name',
    'admin.users.email': 'Email',
    'admin.users.department': 'Department',
    'admin.users.region': 'Region',
    'admin.users.role': 'Role',
    'admin.tests.title': 'Test Management',
    'admin.tests.import': 'Import from Excel',
    'admin.tests.name': 'Name',
    'admin.tests.block': 'Block',
    'admin.tests.questions': 'Questions',
    'admin.tests.timeLimit': 'Time (min)',
    'admin.tests.status': 'Status',
    'admin.tests.statusActive': 'Active',
    'admin.tests.statusInactive': 'Inactive',
    'admin.statistics.details': 'Detailed Statistics',
    'admin.statistics.description': 'Detailed statistics by regions, questions and users will be displayed here',
    'admin.upload.success': 'uploaded successfully',
    'admin.upload.error': 'upload error',
    'admin.upload.selectBlock': 'Select test block',
    'admin.upload.format': 'Format: one sheet with columns: №, Вопрос, Тип, Варианты, Ответ',
    'admin.upload.upload': 'Upload',
    'admin.upload.cancel': 'Cancel',
    'admin.tests.exportTemplate': 'Export Template',
    'admin.tests.delete': 'Delete',
    'admin.tests.deleteConfirm': 'Delete Test',
    'admin.tests.deleteMessage': 'Are you sure you want to delete the test "{title}"? This action cannot be undone.',
    'admin.tests.deleteSuccess': 'Test deleted successfully',
    'admin.tests.deleteError': 'Error deleting test',
    'admin.tests.titleUpdated': 'Test title updated',
    'admin.tests.clickToEditName': 'Click to edit name',
    'admin.tests.actions': 'Actions',
    
    // Test Page
    'test.timeLeft': 'Time Remaining',
    'test.question': 'Question',
    'test.of': 'of',
    'test.back': 'Back',
    'test.next': 'Next',
    'test.checkAnswer': 'Check answer',
    'test.complete': 'Complete Test',
    'test.passed': 'Test Passed!',
    'test.failed': 'Test Failed',
    'test.startError': 'Failed to start test',
    'test.timeUp': 'Time Up',
    'test.timeUpMessage': 'Time for completing the test has expired. The test will be completed automatically.',
    'test.part': 'Part',
    'test.zoom': 'Zoom',
    'test.enterPartName': 'Enter part name',
    'test.enterPartNumber': 'Enter part number',
    'test.enterAnswer': 'Enter your answer',
    'test.matchingInstruction': 'Match elements from the left column with elements from the right column',
    'test.selectMatch': 'Select match',
    'test.noOptions': 'No options available',
    'test.imageIdentification': 'Identify the part and part number from the image:',
    'test.completeError': 'Failed to complete test',
    'test.matchingError': 'Failed to determine matching question structure. Please check question data.',
    'test.multipleChoiceHint': 'Several options possible',
    'common.error': 'Error',
    
    // AI Generator
    'aiGenerator.title': 'AI Test Generator',
    'aiGenerator.description': 'Use artificial intelligence to automatically generate tests based on FEBEST product data, article coding, product categories, and vehicle applicability.',
    'aiGenerator.accessDenied': 'Access Denied',
    'aiGenerator.adminOnly': 'This feature is available only to administrators',
    'aiGenerator.form.title': 'Test Title',
    'aiGenerator.form.titleRequired': 'Enter test title',
    'aiGenerator.form.titlePlaceholder': 'e.g., Article Coding Test',
    'aiGenerator.form.description': 'Test Description',
    'aiGenerator.form.descriptionPlaceholder': 'Test description (optional)',
    'aiGenerator.form.block': 'Test Block',
    'aiGenerator.form.difficulty': 'Difficulty Level',
    'aiGenerator.form.questionsCount': 'Number of Questions',
    'aiGenerator.form.questionsCountPlaceholder': 'From 1 to 100',
    'aiGenerator.form.timeLimit': 'Time Limit (minutes)',
    'aiGenerator.form.timeLimitPlaceholder': 'From 5 to 180 minutes',
    'aiGenerator.difficulty.easy': 'Easy',
    'aiGenerator.difficulty.easyDesc': 'Simple fact-based questions',
    'aiGenerator.difficulty.medium': 'Medium',
    'aiGenerator.difficulty.mediumDesc': 'Questions on understanding and application',
    'aiGenerator.difficulty.hard': 'Hard',
    'aiGenerator.difficulty.hardDesc': 'Complex analysis and matching questions',
    'aiGenerator.generate': 'Generate Test',
    'aiGenerator.generating': 'Generating test...',
    'aiGenerator.generatingDesc': 'Please wait. AI is generating questions based on product data.',
    'aiGenerator.success': 'Test generated successfully!',
    'aiGenerator.error': 'Error generating test',
    
    // Footer
    'footer.text': 'FEBEST Testing System',
    'footer.developedBy': 'Developed by Pikin P.',
  },
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved as Language) || 'ru';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
